<template>
  <ClientOnly>
    <teleport to="body">
      <nav
        class="fixed left-0 right-0 bottom-0 z-[9999] border-t border-black-10 bg-white shadow-up rounded-t-[8px] safe-area-padding desktop:hidden overflow-x-hidden"
        :class="[
          'h-[72px] px-2 sm:px-4',
        ]"
      >
        <ul class="mx-auto flex h-full items-center justify-between gap-1 sm:gap-2 desktop:max-w-screen-desktop desktop:gap-4">
          <li v-for="item in itemsToShow" :key="item.key" class="flex-1">
            <NuxtLink
              :to="localePath(item.to)"
              class="mx-auto flex w-fit items-center rounded-full transition-colors duration-200"
              :class="
                activeKey === item.key
                  ? 'bg-[#FFB648] text-black-100 px-3 sm:px-6 py-[14px] gap-2 sm:gap-[10px]'
                  : 'text-black-90 hover:bg-black-03 px-2 sm:px-4 py-2 gap-1 sm:gap-2 desktop:gap-3 desktop:px-6 desktop:py-3'
              "
              @click="handleItemClick(item, $event)"
            >
              <UIIcon :name="item.icon" class="w-5 h-5 sm:w-6 sm:h-6" />
              <span 
                v-show="activeKey === item.key || isDesktop"
                class="whitespace-nowrap font-medium text-center"
                :class="[
                  activeKey === item.key 
                    ? 'active-label text-xs xs:text-sm leading-none' 
                    : 'text-xs xs:text-sm desktop:text-base'
                ]"
              >
                {{ item.label }}
              </span>
            </NuxtLink>
          </li>
        </ul>
      </nav>
    </teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
import { useTolgee } from '@tolgee/vue'

type Item = {
  key: 'services' | 'prelaunch' | 'explore' | 'portfolio' | 'training'
  label: string
  to: string
  icon: string
}

const route = useRoute()
const localePath = useLocalePath()
const uiStore = useUIStore()

const tolgee = useTolgee()

const items = computed<Item[]>(() => [
  { key: 'services', label: tolgee.value.t('services.title'), to: '/services', icon: 'services' },
  { key: 'prelaunch', label: tolgee.value.t('prelaunch.badge.prelaunch'), to: '/?list=1', icon: 'prelaunch' },
  { key: 'explore', label: tolgee.value.t('navigation.drawer.explore'), to: isDesktop.value ? '/explore' : '/?explore=1', icon: 'explore' },
  { key: 'portfolio', label: tolgee.value.t('portfolio.tabs.title'), to: '/portfolio', icon: 'portfolio' },
  { key: 'training', label: tolgee.value.t('training.title'), to: '/training', icon: 'training' },
])

const { isDesktop } = useAppBreakpoints()

const itemsToShow = computed(() => {
  if (isDesktop.value) {
    return items.value.filter(item => item.key !== 'explore')
  }
  return items.value
})

const paths = computed(() => ({
  home: localePath('/'),
  explore: localePath('/explore'),
  profile: localePath('/portfolio'),
  services: localePath('/services'),
  training: localePath('/training'),
}))

const isSidePanelOpen = computed(() => uiStore.getDynamicSidePanel.value)

const activeKey = computed<Item['key'] | null>(() => {
  if (isSidePanelOpen.value) {
    return null
  }

  const routeName = route.name?.toString() || ''
  const fromQuery = route.query?.from

  const p = route.path

  if (p.startsWith(paths.value.services)) {
    return 'services'
  }

  if (p.startsWith(paths.value.training)) {
    return 'training'
  }

  if (p.startsWith(paths.value.profile)) {
    return route.hash?.includes('training') ? 'training' : 'portfolio'
  }

  if (p.includes('/add-property') || p.includes('/property/')) {
    return 'portfolio'
  }

  if (fromQuery === 'portfolio') {
    return 'portfolio'
  }

  if (routeName.includes('properties-id') || p.includes('/properties/')) {
    const fromQuery = route.query?.from
    if (fromQuery === 'explore') {
      return 'explore'
    }
    return 'prelaunch'
  }

  if (p === paths.value.explore || p === `${paths.value.explore}/`) {
    return 'explore'
  }

  if (p === paths.value.home || p === `${paths.value.home}/`) {
    const hasListQuery = route.query?.list
    const isListView = hasListQuery === '1' || hasListQuery === 'true'
    
    if (isListView) return 'prelaunch'

    const hasExploreQuery = route.query?.explore
    const isMapView = hasExploreQuery === '1' || hasExploreQuery === 'true' || hasExploreQuery === 'map'

    if (isMapView) return 'explore'
    return 'prelaunch'
  }

  return null
})

const handleItemClick = (item: Item, event: Event) => {
  if (item.key === 'explore' && activeKey.value === 'explore') {
    event.preventDefault()
    window.dispatchEvent(new CustomEvent('reload-explore-map'))
  }
}
</script>

<style scoped>
.safe-area-padding {
  padding-bottom: calc(env(safe-area-inset-bottom, 0px));
}

.active-label {
  font-weight: 700;
  line-height: 100%;
  letter-spacing: 0;
}
</style>